import { ConfirmModal, Loading } from '@/components';
import { useExpenditureBudgetPurpose } from '@/features/master-table/hooks';
import { usePolicies } from '@/features/master-table/policy/_hooks/policy';
import { useCheckRemoveNotExistUserIdOrNotExistOrganizationId } from '@/features/project/_hooks/form-control';
import {
  useProject,
  useUpdateProject,
} from '@/features/project/_hooks/project';
import type {
  ProjectFormSchema,
  RsFormSchema,
} from '@/features/project/projects/_models/project';
import { ProjectForm } from '@/features/project/projects/upsert-form/_components/ProjectForm';
import { useRecursiveOrganizationFilteredByKey } from '@/features/tenant-manager/hooks';
import { useId } from '@/hooks';
import { useMessage } from '@/providers';
import { findUpperRecursive } from '@/utils';
import dayjs from 'dayjs';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import invariant from 'tiny-invariant';
import type { z } from 'zod';

export const UpdateProjectPage: FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { openMessage } = useMessage();
  const { id } = useId();
  const { data: projectData } = useProject(id);
  const { mutateAsync: updateProject } = useUpdateProject();
  const {
    openUpdateConfirmModal,
    onCancelModal,
    checkRemoveNotExistUserIdOrNotExistOrganizationId,
  } = useCheckRemoveNotExistUserIdOrNotExistOrganizationId();

  const { data: organizationIds } = useRecursiveOrganizationFilteredByKey({
    params: { ids: [projectData?.organizationId ?? ''] },
    enabled: !!projectData?.organizationId,
    options: { separator: undefined, filterBy: 'id', reverse: true },
    select: (data) => data.flat(),
  });

  const {
    data: nestedRelatedOrganizationIds,
    isLoading: isLoadingNestedRelatedOrganizationIds,
  } = useRecursiveOrganizationFilteredByKey({
    params: { ids: projectData?.relatedOrganizationIds ?? [] },
    enabled: !!projectData?.relatedOrganizationIds.length,
    options: { separator: undefined, filterBy: 'id', reverse: true },
  });

  const {
    data: nestedOtherResponsibleOrganizationIds,
    isLoading: isLoadingNestedOtherResponsibleOrganizationIds,
  } = useRecursiveOrganizationFilteredByKey({
    params: { ids: projectData?.otherResponsibleOrganizationIds ?? [] },
    enabled: !!projectData?.otherResponsibleOrganizationIds.length,
    options: { separator: undefined, filterBy: 'id', reverse: true },
  });

  const { data: policiesData, isLoading: isLoadingPolicies } = usePolicies({
    enabled: projectData?.sheetType === 'RS',
  });

  const expenditureBudgetPurpose = useExpenditureBudgetPurpose({
    id: projectData?.majorExpenditureBudgetPurposeId ?? '',
  });

  if (
    !projectData ||
    !organizationIds ||
    (projectData.relatedOrganizationIds.length &&
      isLoadingNestedRelatedOrganizationIds) ||
    (projectData.otherResponsibleOrganizationIds.length &&
      isLoadingNestedOtherResponsibleOrganizationIds) ||
    (projectData.sheetType === 'RS' && isLoadingPolicies) ||
    (projectData?.majorExpenditureBudgetPurposeId &&
      expenditureBudgetPurpose.isLoading)
  ) {
    return <Loading />;
  }

  const onUpdateProject = async (values: z.infer<typeof ProjectFormSchema>) => {
    const res = await updateProject({
      id,
      ...values,
    });
    onCancelModal();
    if (res.baseOrganizationChanged) {
      await openMessage(
        'success',
        t('change-organization-of-project-completed'),
      );
    }
    const isUpdateMajorExpenditureBudgetPurpose =
      projectData.majorExpenditureBudgetPurposeId !==
      res.majorExpenditureBudgetPurposeId;
    await Promise.all([
      isUpdateMajorExpenditureBudgetPurpose
        ? openMessage(
            'warning',
            t(
              'warning-message-change-major-expenditure-budget-update-completed',
            ),
          )
        : openMessage('success', t('message-update-completed')),
      (async () => {
        navigate(location.pathname.split('/').slice(0, -1).join('/'));
      })(),
    ]);
  };

  return (
    <>
      <ProjectForm
        type="edit"
        ignoredIdsForRelatedProject={[id]}
        openUpdateConfirmModal={openUpdateConfirmModal.isOpen}
        initialValues={
          projectData.sheetType === 'RS'
            ? {
                sheetType: 'RS',
                data: {
                  // FIXME: zod parseするか、propertyを一個ずつ渡して型エラーをなくしてください
                  ...(projectData as unknown as z.infer<
                    typeof RsFormSchema
                  >['data']),
                  skipAdministrativeReformTeamEvaluation:
                    projectData.skipAdministrativeReformTeamEvaluation,
                  fiscalYear: dayjs().year(projectData.fiscalYear),
                  period: [
                    projectData.startYear
                      ? dayjs().year(projectData.startYear)
                      : undefined,
                    projectData.endYear
                      ? dayjs().year(projectData.endYear)
                      : undefined,
                  ],
                  organizationIds,
                  nestedRelatedOrganizationIds,
                  policiesForProject: projectData.policiesForProject.map(
                    (policyForProject) => {
                      const currentPolicies = findUpperRecursive(
                        policyForProject.policyId,
                        policiesData ?? [],
                      ).reverse();
                      return {
                        policyId: currentPolicies.map((d) => d.id),
                        policyUrl: policyForProject.policyUrl,
                      };
                    },
                  ),
                  mainCostIds: projectData.mainCosts.map(
                    (mainCost) => mainCost.id,
                  ),
                  relatedProjects: projectData.relatedProjects.map(
                    (relatedProject) => ({
                      id: relatedProject.id,
                      relationType: relatedProject.relationType,
                    }),
                  ),
                  nestedOtherResponsibleOrganizationIds,
                  majorExpenditureBudgetPurposeIds: [
                    expenditureBudgetPurpose.data?.accountingId,
                    expenditureBudgetPurpose.data?.jurisdictionId,
                    expenditureBudgetPurpose.data?.accountingType.type ===
                    'general'
                      ? expenditureBudgetPurpose.data.organizationId
                      : expenditureBudgetPurpose.data?.accountTitleId,
                    expenditureBudgetPurpose.data?.expenditurePurpose.upperId,
                    expenditureBudgetPurpose.data?.id,
                  ].flatMap((v) => v ?? []),
                },
              }
            : undefined
        }
        onSubmit={async (values) => {
          const { isRemovedNotExistValue } =
            await checkRemoveNotExistUserIdOrNotExistOrganizationId(values);
          if (!isRemovedNotExistValue) {
            await onUpdateProject(values);
          }
        }}
      />
      <ConfirmModal
        okText={t('save')}
        open={openUpdateConfirmModal.isOpen}
        title={t('save-confirmation')}
        disclaimerText={t('change-not-exist-in-value-confirmation')}
        onOk={async () => {
          invariant(
            openUpdateConfirmModal.isOpen,
            'The modal is submit with the modal closed.',
          );
          await onUpdateProject(openUpdateConfirmModal.values);
        }}
        onCancel={onCancelModal}
        skipCheck
      />
    </>
  );
};
